import { photoForPlacement } from "../photoPlacement.js";
import { useBandPhotos } from "./useBrandAssets.js";
import { useMediaObjectUrl } from "./useMedia.js";

/**
 * The band press photo that backs every dashboard hero: whichever FILLED slot holds the
 * **Dashboard Page** placement — explicitly assigned in Assets → Brand, or by its slot default
 * (Live Photo, slot 3). Exactly the rule the Assets HERO tag uses, from the same module, so the two
 * surfaces can never disagree about which photo the band chose.
 *
 * This is our equivalent of the legacy's `placements.dashboard`, and it supersedes the slot-order chain the
 * legacy fell back on (`photo3 → photo2 → photo1`): our slot defaults already encode that preference, and
 * binding by slot number ignored explicit assignments — a band that made Main Photo its headline kept seeing
 * the Live photo.
 *
 * Resolution goes through {@link useMediaObjectUrl}, not the payload `url` alone: `TryPublicUrl` yields NULL
 * wherever no public media host is configured (every local dev box), and the authenticated `/raw` path is the
 * app-wide degrade. Full size rather than a rendition — `sm` caps at 720px, which is soft across a hero that
 * is ~1360 CSS px wide on desktop.
 *
 * Returns `null` while resolving, when no photo holds the placement, and when the bytes fail — the hero then
 * keeps its gradient-only look rather than flashing a placeholder or a broken image.
 */
export function useSpotlightPhoto(): string | null {
  const q = useBandPhotos();
  const photos = q.data?.photos;
  const chosen = photos && photoForPlacement(photos, "Dashboard Page");
  const media = useMediaObjectUrl(chosen?.mediaId, chosen?.url);
  return media.url ?? null;
}
